import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { X, MagnifyingGlass, Spinner, Check, Hand } from "@phosphor-icons/react";
import { toast } from "sonner";
import { forwardGeocode } from "../lib/geo";

const SORRISO = { lat: -12.545, lng: -55.711 };

const _distKm = (a, b) => {
  const R = 6371, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const PIN_HTML =
  '<div style="width:30px;height:30px;transform:translateY(-6px)">' +
  '<svg viewBox="0 0 24 24" width="30" height="30" fill="#FF6B35" stroke="#fff" stroke-width="1.5">' +
  '<path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7z"/>' +
  '<circle cx="12" cy="9" r="2.5" fill="#fff"/></svg></div>';

// Mapa interativo (Leaflet/OSM) para o usuário fixar o pino no local exato.
// props: { initial:{lat,lng}|null, fallbackCenter:{lat,lng}|null, fallbackZoom:number,
//          addressQuery:string, onConfirm({lat,lng}), onClose() }
export default function MapPicker({ initial, fallbackCenter, fallbackZoom = 14, addressQuery, onConfirm, onClose }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [pos, setPos] = useState(initial && initial.lat ? { lat: initial.lat, lng: initial.lng } : null);
  const [search, setSearch] = useState(addressQuery || "");
  const [busy, setBusy] = useState(false);

  const moveTo = (lat, lng, zoom = 17) => {
    if (!mapRef.current) return;
    mapRef.current.setView([lat, lng], zoom);
    markerRef.current.setLatLng([lat, lng]);
    setPos({ lat, lng });
  };

  const doSearch = async (q, guardFrom = null) => {
    const query = (q ?? search ?? "").trim();
    if (!query) return;
    setBusy(true);
    try {
      const r = await forwardGeocode(query);
      if (r && r.lat) {
        // V18.9.17 — proteção: o Nominatim às vezes devolve um ponto em outra cidade
        // para endereços incompletos. Se já temos um centro confiável (coordenadas do
        // CEP) e o resultado está absurdamente longe, mantemos o centro do CEP.
        if (guardFrom && _distKm(guardFrom, r) > 30) {
          toast.warning("Confirme o pino: o endereço digitado não foi localizado com precisão.");
          return;
        }
        moveTo(r.lat, r.lng);
      } else if (!guardFrom) {
        toast.warning("Endereço não encontrado no mapa. Arraste o pino até o local certo.");
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const hasInitial = initial && initial.lat;
    // V18.9.12 — sem pino salvo, centraliza na localização atual do app (cidade do
    // usuário) em vez de sempre cair em Sorriso.
    const hasFallback = !hasInitial && fallbackCenter && fallbackCenter.lat;
    const start = hasInitial ? initial : (hasFallback ? fallbackCenter : SORRISO);
    const zoom = hasInitial ? 17 : (hasFallback ? fallbackZoom : 12);
    const map = L.map(mapEl.current, { zoomControl: true }).setView([start.lat, start.lng], zoom);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);
    const icon = L.divIcon({ className: "aj-map-pin", html: PIN_HTML, iconSize: [30, 30], iconAnchor: [15, 30] });
    const marker = L.marker([start.lat, start.lng], { draggable: true, icon }).addTo(map);
    marker.on("dragend", () => { const ll = marker.getLatLng(); setPos({ lat: ll.lat, lng: ll.lng }); });
    map.on("click", (e) => { marker.setLatLng(e.latlng); setPos({ lat: e.latlng.lat, lng: e.latlng.lng }); });
    mapRef.current = map;
    markerRef.current = marker;
    // Com centro confiável (CEP/localização), o pino já vale como resposta — o usuário
    // só faz o ajuste fino em vez de precisar tocar no mapa para liberar o Confirmar.
    if (hasFallback) setPos({ lat: fallbackCenter.lat, lng: fallbackCenter.lng });
    // V18.9.17 — o timeout precisa ser cancelado no cleanup: se o componente
    // desmonta antes dos 250ms, `invalidateSize()` roda num mapa já destruído e
    // estoura "Cannot read properties of undefined (reading '_leaflet_pos')".
    const sizeTimer = setTimeout(() => { if (mapRef.current) map.invalidateSize(); }, 250);
    // sem pino inicial mas com endereço digitado → tenta centralizar pelo geocoding
    if (!hasInitial && addressQuery) doSearch(addressQuery, hasFallback ? fallbackCenter : null);
    return () => { clearTimeout(sizeTimer); map.remove(); mapRef.current = null; markerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex flex-col" data-testid="map-picker">
      <div className="bg-white flex items-center justify-between p-3 gap-2 shadow pt-safe">
        <h3 className="font-heading font-bold text-base flex-shrink-0">Marque no mapa</h3>
        <div className="relative flex-1">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doSearch(); } }}
            placeholder="Buscar endereço no mapa..."
            className="aj-input pr-9 text-sm"
            data-testid="map-search-input"
          />
          <button type="button" onClick={() => doSearch()} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#004E98]" data-testid="map-search-btn">
            <MagnifyingGlass size={18} weight="bold" />
          </button>
        </div>
        <button type="button" onClick={onClose} className="p-1 text-slate-500 flex-shrink-0" data-testid="map-close"><X size={22} weight="bold" /></button>
      </div>

      <div className="relative flex-1">
        <div ref={mapEl} className="absolute inset-0" data-testid="map-canvas" />
        {busy && (
          <div className="absolute inset-0 z-[20] bg-white/70 backdrop-blur-sm flex flex-col items-center justify-center gap-2" data-testid="map-busy">
            <Spinner size={36} className="animate-spin text-[#004E98]" />
            <span className="font-bold text-slate-700">Buscando...</span>
          </div>
        )}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[15] bg-black/70 text-white text-[11px] px-3 py-1.5 rounded-full flex items-center gap-1.5 pointer-events-none">
          <Hand size={13} weight="fill" /> Toque no mapa ou arraste o pino até sua casa
        </div>
      </div>

      <div className="bg-white p-3 space-y-2 shadow-inner pb-safe">
        {pos && (
          <div className="text-[11px] text-slate-500 text-center" data-testid="map-coords">📍 {pos.lat.toFixed(5)}, {pos.lng.toFixed(5)}</div>
        )}
        <button
          type="button"
          disabled={!pos}
          onClick={() => { onConfirm({ lat: pos.lat, lng: pos.lng }); onClose(); }}
          className="btn-primary w-full disabled:opacity-50"
          data-testid="map-confirm-btn"
        >
          <Check size={18} weight="bold" /> Confirmar este local
        </button>
      </div>
    </div>
  );
}
